import React from 'react';
import {
    Box,
    Dialog,
    Typography,
    SxProps,
    Tabs,
    Tab,
    IconButton,
    List,
    ListItem,
    Divider,
    Stack,
    TextField,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { headingFontBold, vars } from '../../../Theme';
import { useTranslation } from 'react-i18next';
import {
    ISettingsDataObject,
    ISettingsDialogTabData,
} from '../Header/UserControl/UserSettingsDialogConfig';
import { useParams } from 'react-router-dom';

/** Styles for Dialog's component. */
const dialogSx: SxProps = {
    '& .MuiDialog-paper': {
        width: '80%',
        height: '60%',
        minWidth: '350px',
        overflow: 'hidden',
    },
};

/** Styles for Dialog's content. */
const dialogContentSx: SxProps = {
    flexGrow: 1,
    display: 'flex',
};

/** Styles for Dialog's tabs. */
const tabsSx: SxProps = {
    borderRight: 1,
    borderColor: vars.lightGray,
    padding: ' 0.5rem',
    flex: '0 0 auto',
};

/** Styles for Dialog's tab. */
const tabSx: SxProps = {
    ...headingFontBold,
    width: '100%',
    textTransform: 'capitalize',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    minHeight: 'auto',
    fontSize: 16,
    color: vars.font,
    p: 1.5,
    borderRadius: '4px',
    '&:hover': {
        backgroundColor: vars.fillGray,
    },
    '&.Mui-selected': {
        color: vars.mainGreen,

        '&:hover': {
            color: vars.selectedHoveredGreen,
            backgroundColor: vars.bgGreenHover,
        },
    },
    '& .MuiTab-icon': {
        mb: 0,
        mr: 1,
        width: '24px',
        height: '24px',
    },
};

/** Styles for common TextField component. */
const textFieldSx: SxProps = {
    flex: 1,
    '.MuiInput-root.MuiInputBase-root': {
        '&:after': {
            borderBottom: 'none',
            color: 'rgba(0, 0, 0, 0.42)',
        },

        '&:hover': {
            '&:before': {
                borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
            },
        },
    },
    'label.Mui-focused': {
        color: 'rgba(0, 0, 0, 0.6)',
    },
};

/**
 * TabPanel Component for each tab.
 */
const TabPanel = ({ data, title, value, index, onClose, ...other }: ITabPanelProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'header' });

    return (
        <Box
            role='tabpanel'
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            sx={{ flexGrow: 1, p: '0.5rem', overflowY: 'auto', contain: 'size' }}
            {...other}
        >
            {value === index && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant='h6'
                            sx={{
                                ...headingFontBold,
                                fontSize: 18,
                                color: vars.font,
                                textTransform: 'capitalize',
                            }}
                        >
                            {t(title)}
                        </Typography>
                        <IconButton aria-label='close' onClick={onClose}>
                            <Icon path={mdiClose} size={1} />
                        </IconButton>
                    </Box>
                    <Divider />
                    <List>
                        {data.map(d => {
                            return (
                                <ListItem
                                    key={d.label}
                                    sx={{
                                        alignItems: d?.viewItemComponentProps?.align ?? 'center',
                                        flexDirection:
                                            d?.viewItemComponentProps?.direction ?? 'row',
                                    }}
                                >
                                    {d.label !== 'stamps' && d.label !== 'signature' && (
                                        <Stack sx={{ flex: 1, width: '100%' }}>
                                            <TextField
                                                key={`field_${d.label}`}
                                                id={`field_${d.label}`}
                                                label={d.hideLabel ? '' : t(d.label)}
                                                defaultValue={d.value}
                                                variant='standard'
                                                fullWidth
                                                multiline={d?.viewItemComponentProps?.multiline}
                                                slotProps={{
                                                    input: {
                                                        readOnly: true,
                                                    },
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    htmlInput: {
                                                        ...(d?.viewItemComponentProps?.inputProps
                                                            ? d.viewItemComponentProps.inputProps
                                                            : {}),
                                                    },
                                                }}
                                                sx={textFieldSx}
                                            />
                                        </Stack>
                                    )}
                                    {d.children && d.children()}
                                </ListItem>
                            );
                        })}
                    </List>
                </>
            )}
        </Box>
    );
};

/**
 * Dialog Component that provides settings info.
 */
export default ({ open, handleClose, data }: SettingsDialogProps) => {
    /** State slice that uses to switch tabs. */
    const [value, setValue] = React.useState(0);

    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'header' });

    const { replicaId } = useParams();

    /**
     * Switches tabs.
     * @param event
     * @param newValue New Tab's value.
     */
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    return (
        <Dialog onClose={handleClose} open={open} sx={dialogSx}>
            <Box sx={dialogContentSx}>
                <Tabs
                    orientation='vertical'
                    variant='scrollable'
                    value={value}
                    onChange={handleChange}
                    sx={tabsSx}
                >
                    {Object.keys(data).map(tab => {
                        if (tab === 'signature' && !replicaId) {
                            return <></>;
                        }
                        return (
                            <Tab
                                key={tab}
                                icon={<Icon path={data[tab].icon} size={1} />}
                                label={t(data[tab].name)}
                                id={`vertical-tab-${tab}`}
                                aria-controls={`vertical-tabpanel-${tab}`}
                                sx={tabSx}
                            />
                        );
                    })}
                </Tabs>
                {Object.keys(data).map((tab, index) => (
                    <TabPanel
                        key={tab}
                        data={data[tab].data}
                        title={data[tab].name}
                        value={value}
                        index={index}
                        onClose={handleClose}
                    />
                ))}
            </Box>
        </Dialog>
    );
};

/**
 * Interface that describes TabPanel's props.
 */
interface ITabPanelProps {
    title: string;
    data: ISettingsDialogTabData[];
    index: number;
    value: number;
    onClose: () => void;
}

/**
 * Interface that describes SettingsDialog's props.
 */
interface SettingsDialogProps {
    open: boolean;
    handleClose: () => void;
    data: ISettingsDataObject;
}
